import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';

const FlappyBirdGame = () => {
  const { isDark } = useTheme();

  const [birdPosition, setBirdPosition] = useState(150);
  const [birdVelocity, setBirdVelocity] = useState(0);
  const [pipes, setPipes] = useState([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [highScore, setHighScore] = useState(0);

  const gravity = 0.45;
  const jumpStrength = -7.5;
  const pipeWidth = 48;
  const gapHeight = 125;
  const gameHeight = 360;
  const gameWidth = 280;

  // Reset Game
  const initGame = useCallback(() => {
    setBirdPosition(150);
    setBirdVelocity(0);
    setPipes([]);
    setScore(0);
    setGameOver(false);
    setGameStarted(false);
  }, []);

  // Mekanisme Lompat
  const handleJump = useCallback(() => {
    if (!gameStarted && !gameOver) {
      setGameStarted(true);
    }
    if (gameStarted && !gameOver) {
      setBirdVelocity(jumpStrength);
    }
    if (gameOver) {
      initGame();
    }
  }, [gameStarted, gameOver, initGame]);

  // Kontrol Keyboard
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        handleJump();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleJump]);

  // Game Physics Loop
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const gameLoop = setInterval(() => {
      // Posisi Burung
      setBirdPosition(prev => {
        const newPosition = prev + birdVelocity;
        setBirdVelocity(v => v + gravity);

        // Tabrakan Tanah
        if (newPosition >= gameHeight - 50) {
          setGameOver(true);
          setHighScore(h => Math.max(h, score));
          return gameHeight - 50;
        }

        // Tabrakan Atap
        if (newPosition <= 0) return 0;

        return newPosition;
      });

      // Pipa
      setPipes(prevPipes => {
        const newPipes = prevPipes
          .map(p => ({ ...p, x: p.x - 2 }))
          .filter(p => p.x > -pipeWidth);

        // Tambah Pipa Baru
        if (newPipes.length === 0 || newPipes[newPipes.length - 1].x < gameWidth - 170) {
          const gapPosition = Math.random() * (gameHeight - gapHeight - 120) + 40;
          newPipes.push({
            x: gameWidth,
            gapPosition,
            passed: false
          });
        }

        // Cek Poin & Tabrakan
        newPipes.forEach(pipe => {
          if (!pipe.passed && pipe.x + pipeWidth < 40) {
            pipe.passed = true;
            setScore(s => s + 1);
          }

          if (
            pipe.x < 65 && pipe.x + pipeWidth > 35 &&
            (birdPosition < pipe.gapPosition || birdPosition + 24 > pipe.gapPosition + gapHeight)
          ) {
            setGameOver(true);
            setHighScore(h => Math.max(h, score));
          }
        });

        return newPipes;
      });
    }, 20);

    return () => clearInterval(gameLoop);
  }, [gameStarted, gameOver, birdVelocity, score, gameHeight]);

  // Token Warna Tema
  const titleClr  = isDark ? 'text-white' : 'text-gray-800';
  const textMuted = isDark ? 'text-slate-400' : 'text-gray-500';
  const scoreClr  = isDark ? 'text-blue-400' : 'text-blue-600';

  const skyBg     = isDark ? 'bg-[#060B14]' : 'bg-sky-200';
  const pipeColor = isDark ? 'bg-emerald-600 border-emerald-400' : 'bg-emerald-500 border-emerald-700';
  const groundBg   = isDark ? 'bg-[#111C2C] border-t-2 border-[#1E3A5F]' : 'bg-amber-800 border-t-2 border-amber-900';

  return (
    <div className="max-w-md mx-auto text-center select-none">
      <h3 className={`text-xl md:text-2xl font-bold mb-4 pixel-font ${titleClr}`}>
        🐦 FLAPPY BIRD
      </h3>

      {/* Scoreboard */}
      <div className="grid grid-cols-2 gap-4 mb-4 bg-[var(--bg-muted)] p-3 pixel-border max-w-[280px] mx-auto">
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>SKOR</div>
          <div className={`text-lg font-bold ${scoreClr}`}>{score}</div>
        </div>
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>TERBAIK</div>
          <div className="text-lg font-bold text-amber-500">{highScore}</div>
        </div>
      </div>

      {/* Canvas Area Game */}
      <div
        className={`relative mx-auto pixel-border overflow-hidden cursor-pointer ${skyBg}`}
        style={{ width: `${gameWidth}px`, height: `${gameHeight}px` }}
        onClick={handleJump}
      >
        {/* Karakter Burung */}
        <div
          className="absolute bg-amber-400 border-2 border-amber-600 rounded-full flex items-center justify-center font-bold text-xs shadow-sm transition-transform"
          style={{
            left: '40px',
            top: `${birdPosition}px`,
            width: '26px',
            height: '26px',
            transform: `rotate(${Math.min(Math.max(birdVelocity * 4, -30), 70)}deg)`
          }}
        >
          
        </div>

        {/* Pipa-pipa */}
        {pipes.map((pipe, index) => (
          <React.Fragment key={index}>
            {/* Pipa Atas */}
            <div
              className={`absolute border-b-2 border-x-2 ${pipeColor}`}
              style={{
                left: `${pipe.x}px`,
                width: `${pipeWidth}px`,
                top: 0,
                height: `${pipe.gapPosition}px`
              }}
            />
            {/* Pipa Bawah */}
            <div
              className={`absolute border-t-2 border-x-2 ${pipeColor}`}
              style={{
                left: `${pipe.x}px`,
                width: `${pipeWidth}px`,
                top: `${pipe.gapPosition + gapHeight}px`,
                height: `${gameHeight - pipe.gapPosition - gapHeight}px`
              }}
            />
          </React.Fragment>
        ))}

        {/* Tanah Bawah */}
        <div className={`absolute bottom-0 w-full h-10 ${groundBg} flex items-center justify-center text-[10px] font-mono opacity-80`}>
          🌴 🌴 🌴
        </div>

        {/* Overlay Mulai Game */}
        {!gameStarted && !gameOver && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-xs">
            <div className={`pixel-border p-4 text-center ${isDark ? 'bg-[#0D1520] text-white' : 'bg-white text-gray-800'}`}>
              <div className="text-xs pixel-font mb-3">TAP / SPASI UNTUK TERBANG</div>
              <button
                className="pixel-button bg-emerald-600 text-white px-4 py-1.5 text-xs font-bold pixel-font hover:bg-emerald-500"
                onClick={handleJump}
              >
                MULAI
              </button>
            </div>
          </div>
        )}

        {/* Overlay Game Over */}
        {gameOver && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-xs">
            <div className={`pixel-border p-4 text-center ${isDark ? 'bg-[#0D1520] text-white' : 'bg-white text-gray-800'}`}>
              <div className="text-xs font-bold text-rose-500 pixel-font mb-2">GAME OVER!</div>
              <div className="text-xs mb-1">Skor: {score}</div>
              <div className="text-xs text-amber-500 mb-3">Terbaik: {highScore}</div>
              <button
                className="pixel-button bg-emerald-600 text-white px-4 py-1.5 text-xs font-bold pixel-font hover:bg-emerald-500"
                onClick={initGame}
              >
                MAIN LAGI
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={`text-xs ${textMuted} mt-3`}>
        Sentuh layar atau tekan Spasi untuk mengepakkan sayap
      </div>
    </div>
  );
};

export default FlappyBirdGame;